'use client';

import { c as _c } from "react/compiler-runtime";
import * as ENGINE from '@gnsx/genesys.js';
import { AlertTriangleIcon, AppWindowIcon, ChevronDownIcon, CloudUploadIcon, FlaskConicalIcon, Loader2Icon, LogOutIcon, MoreHorizontalIcon, NetworkIcon, PictureInPicture2Icon, SaveIcon, SunIcon, TabletSmartphoneIcon, WandSparklesIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { usePathname } from 'next/navigation';
import { Fragment, useCallback, useState } from 'react';
import { UserAvatar } from '../UserAvatar';
import { PlayModeLaunchMenu } from './PlayModeLaunchMenu';
import { PlayModeTooltipContent } from './PlayModeTooltipContent';
import { PlayPreviewSettingsDialog } from './PlayPreviewSettingsDialog';
import { TitleBarButton } from './TitleBarButton';
import { isEditorTitleBarContext, shouldShowPlayModeControls } from './titleBarContext';
import type { PlayLaunchMode } from '@/editor/core/play-preview/playPreviewTypes';
import type { User as FirebaseUser } from 'firebase/auth';
import type { ComponentType, ReactNode } from 'react';
import { editorActions } from '@/app/editor/core';
import { USE_SDK_AGENT_CHAT } from '@/app/editor/featureFlags';
import { globalStore } from '@/app/globalStore';
import { platform, sdk } from '@/app/sdk';
import { IconAgentNova, IconDevTools, IconPlay } from '@/components/icons';
import { ToolbarIconButton } from '@/components/ui/button-presets';
import { Checkbox } from '@/components/ui/checkbox';
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Kbd } from '@/components/ui/kbd';
import { Slider } from '@/components/ui/slider';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useBuildLightmapDialog } from '@/components/useBuildLightmapDialog';
import { useAuth } from '@/context/AuthContext';
import { ScenarioGenerationDialog } from '@/editor/components/scenario';
import { editorScene, project, sceneStore, store } from '@/editor/store';
import { editorStore } from '@/editor/store/editorStore';
import { scenarioConfig } from '@/lib/scenario';
import { getCtrlOrCmdKeyModifier, IS_DEV } from '@/lib/utils';
const Separator = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="h-4 w-0.5 mx-1 bg-border" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const COMPACT_VIEW = true;
const ctrlOrCmd = getCtrlOrCmdKeyModifier(platform);

/** Icons must match `PlayModeLaunchMenu` radio items. */
const PLAY_LAUNCH_MODE_META = {
  embedded: {
    label: 'Play in Editor',
    Icon: PictureInPicture2Icon
  },
  new_window: {
    label: 'Play in New Window',
    Icon: AppWindowIcon
  },
  device_emulation: {
    label: 'Play in Mobile Preview',
    Icon: TabletSmartphoneIcon
  }
} as const;
type LagSimulationTarget = 'server' | 'client';
type LagSimulationSectionProps = {
  title: string;
  target: LagSimulationTarget;
  enabled: boolean;
  latencyMs: number;
  jitterMs: number;
  packetLossPercent: number;
};
const LagSimulationSection = t0 => {
  const $ = _c(33);
  const {
    title,
    target,
    enabled,
    latencyMs,
    jitterMs,
    packetLossPercent
  } = t0;
  let t1;
  if ($[0] !== target) {
    t1 = value => store.setMultiplayerLagEnabled(target, value === true);
    $[0] = target;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== enabled || $[3] !== t1) {
    t2 = <Checkbox size="xs" checked={enabled} onCheckedChange={t1} />;
    $[2] = enabled;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== title) {
    t3 = <span>{title} Lag Simulation</span>;
    $[5] = title;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <label className="flex items-center gap-2 text-xs font-medium">{t2}{t3}</label>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = !enabled;
  let t6;
  if ($[10] !== target) {
    t6 = value_0 => store.setMultiplayerLagLatencyMs(target, value_0);
    $[10] = target;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== latencyMs || $[13] !== t5 || $[14] !== t6) {
    t7 = <LagSimulationSlider label="Latency" value={latencyMs} max={500} suffix="ms" disabled={t5} onValueChange={t6} />;
    $[12] = latencyMs;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = !enabled;
  let t9;
  if ($[16] !== target) {
    t9 = value_1 => store.setMultiplayerLagJitterMs(target, value_1);
    $[16] = target;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== jitterMs || $[19] !== t8 || $[20] !== t9) {
    t10 = <LagSimulationSlider label="Jitter" value={jitterMs} max={100} suffix="ms" disabled={t8} onValueChange={t9} />;
    $[18] = jitterMs;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const t11 = !enabled;
  let t12;
  if ($[22] !== target) {
    t12 = value_2 => store.setMultiplayerLagPacketLossPercent(target, value_2);
    $[22] = target;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== packetLossPercent || $[25] !== t11 || $[26] !== t12) {
    t13 = <LagSimulationSlider label="Packet Loss" value={packetLossPercent} max={100} suffix="%" disabled={t11} onValueChange={t12} />;
    $[24] = packetLossPercent;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t10 || $[29] !== t13 || $[30] !== t4 || $[31] !== t7) {
    t14 = <div className="flex flex-col gap-2 px-2 py-2" onPointerDown={_temp} onKeyDown={_temp2}>{t4}{t7}{t10}{t13}</div>;
    $[28] = t10;
    $[29] = t13;
    $[30] = t4;
    $[31] = t7;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  return t14;
};
LagSimulationSection.displayName = 'LagSimulationSection';
type LagSimulationSliderProps = {
  label: string;
  value: number;
  max: number;
  suffix: string;
  disabled: boolean;
  onValueChange: (value: number) => void;
};
const LagSimulationSlider = t0 => {
  const $ = _c(21);
  const {
    label,
    value,
    max,
    suffix,
    disabled,
    onValueChange
  } = t0;
  const t1 = disabled ? "space-y-1 opacity-50" : "space-y-1";
  let t2;
  if ($[0] !== label) {
    t2 = <span className="text-xs text-muted-foreground">{label}</span>;
    $[0] = label;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== suffix || $[3] !== value) {
    t3 = <span className="text-xs font-semibold tabular-nums">{value}{suffix}</span>;
    $[2] = suffix;
    $[3] = value;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <div className="flex items-center justify-between">{t2}{t3}</div>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== value) {
    t5 = [value];
    $[8] = value;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== onValueChange) {
    t6 = vals => onValueChange(vals[0] ?? 0);
    $[10] = onValueChange;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== disabled || $[13] !== max || $[14] !== t5 || $[15] !== t6) {
    t7 = <Slider min={0} max={max} step={1} value={t5} disabled={disabled} onValueChange={t6} />;
    $[12] = disabled;
    $[13] = max;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t1 || $[18] !== t4 || $[19] !== t7) {
    t8 = <div className={t1}>{t4}{t7}</div>;
    $[17] = t1;
    $[18] = t4;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
};
LagSimulationSlider.displayName = 'LagSimulationSlider';

/** Left side controls for the title bar (Save) */
export const TitleBarButtonsLeft = observer(() => {
  const pathname = usePathname();
  const isPlayMode = editorScene.isPlayMode;
  const isPlayModeStarting = editorScene.isPlayModeStarting;
  const projectOpened = project.isProjectOpened;
  const inEditorContext = isEditorTitleBarContext(pathname, projectOpened);
  const hasLoadedScene = !!project.currentScene;
  const hasBuildErrors = globalStore.hasBuildErrors;
  const hasPlayModeErrors = globalStore.hasAnyPlayModeErrors;
  const buildErrorCount = globalStore.buildErrorCount;
  const playModeErrorCount = globalStore.playModeErrorCount;
  const shouldShowErrorButton = isPlayMode ? hasPlayModeErrors : hasBuildErrors || hasPlayModeErrors;
  const totalErrorCount = isPlayMode ? playModeErrorCount : buildErrorCount + playModeErrorCount;
  const canSave = inEditorContext && hasLoadedScene && !isPlayMode;
  const toggleEditorErrorOverlay = () => {
    if (hasBuildErrors && hasPlayModeErrors) {
      const show = !(globalStore.showBuildErrorOverlay || globalStore.showPlayModeErrorOverlay);
      globalStore.setShowBuildErrorOverlay(show);
      globalStore.setShowPlayModeErrorOverlay(show);
      return;
    }
    if (hasBuildErrors) {
      globalStore.setShowBuildErrorOverlay(!globalStore.showBuildErrorOverlay);
      return;
    }
    if (hasPlayModeErrors) {
      globalStore.setShowPlayModeErrorOverlay(!globalStore.showPlayModeErrorOverlay);
    }
  };
  const handleErrorButtonClick = () => {
    if (isPlayMode) {
      if (sdk?.playMode) {
        if (sdk.playMode.toggleErrorOverlay) {
          sdk.playMode.toggleErrorOverlay();
          return;
        }
        console.warn('[App] SDK compatibility: playMode.toggleErrorOverlay is unavailable, falling back to editor overlay');
      }
      toggleEditorErrorOverlay();
      return;
    }
    toggleEditorErrorOverlay();
  };
  const errorTooltip = (() => {
    if (isPlayMode) {
      return `Play Mode Error${playModeErrorCount === 1 ? '' : 's'}${playModeErrorCount > 1 ? ` (${playModeErrorCount})` : ''} - Click to view`;
    }
    if (hasBuildErrors && hasPlayModeErrors) {
      return `Errors (${totalErrorCount}) - Click to view`;
    }
    if (hasBuildErrors) {
      return `Build Error${buildErrorCount === 1 ? '' : 's'}${buildErrorCount > 1 ? ` (${buildErrorCount})` : ''} - Click to view`;
    }
    return `Play Mode Error${playModeErrorCount === 1 ? '' : 's'}${playModeErrorCount > 1 ? ` (${playModeErrorCount})` : ''} - Click to view`;
  })();
  if (!inEditorContext && !isPlayMode && !isPlayModeStarting) return null;
  return <>
      {!isPlayMode && <Separator />}

      {/* Save button */}
      {!isPlayMode && <TitleBarButton onClick={() => editorActions.editorSave(true, true)} disabled={!canSave || store.isSaving} tooltip={`Save Scene (${ctrlOrCmd}+S)`} pressed={store.isSaving}>
          {store.isSaving ? <Loader2Icon className="size-4 animate-spin" /> : <SaveIcon className="size-4" />}
        </TitleBarButton>}

      {/* Build/play mode error alert button */}
      {shouldShowErrorButton && <ToolbarIconButton onClick={handleErrorButtonClick} tooltip={errorTooltip} className="titlebarControls relative text-destructive hover:text-destructive/80 animate-pulse">
          <AlertTriangleIcon className="size-5.5" />
          {totalErrorCount > 1 && <span className="absolute -top-1 -right-1 text-destructive text-xs font-mono min-w-4 h-4 flex items-center justify-center">
              {totalErrorCount < 100 ? totalErrorCount : '99+'}
            </span>}
        </ToolbarIconButton>}
    </>;
});
TitleBarButtonsLeft.displayName = 'TitleBarButtonsLeft';

/** Context passed to title bar action visibility and tooltip logic */
type TitleBarActionContext = {
  editorOpened: boolean;
  isPlayMode: boolean;
  projectLoaded: boolean;
  firebaseUser: FirebaseUser | null;
  isDev: boolean;
  isAgentNovaDevEnabled: boolean;
  projectName: string;
  isAgentChatOpen: boolean;
  isSkyboxDialogOpen: boolean;
  isDevPanelOpen: boolean;
  isUploadProjectDialogOpen: boolean;
};

/** State passed into getTitleBarRightActions (setters and store refs) */
type TitleBarRightState = {
  setIsSkyboxDialogOpen: (v: boolean | ((prev: boolean) => boolean)) => void;
  onBuildLightmapClick: () => Promise<void>;
};
type TitleBarActionConfig = {
  id: string;
  visible: (ctx: TitleBarActionContext) => boolean;
  label: string;
  icon: ComponentType<{
    className?: string;
  }>;
  tooltip: ReactNode | string | ((ctx: TitleBarActionContext) => ReactNode | string);
  disabled?: (ctx: TitleBarActionContext) => boolean;
  pressed?: (ctx: TitleBarActionContext) => boolean;
  onClick: () => void;
  separatorBefore?: boolean;
  customRender?: () => ReactNode;
};

/** Build the ordered list of right-side title bar actions (visibility filtered in component). */
function getTitleBarRightActions(ctx: TitleBarActionContext, state: TitleBarRightState): TitleBarActionConfig[] {
  return [{
    id: 'agent-nova',
    label: 'Agent Nova',
    icon: IconAgentNova,
    visible: c => c.editorOpened && !c.isPlayMode && USE_SDK_AGENT_CHAT && c.isAgentNovaDevEnabled,
    tooltip: c => {
      const label = c.isAgentChatOpen ? 'Hide Agent Nova' : 'Show Agent Nova';
      return <span className="inline-flex items-center gap-2">
            {label}
            <Kbd>1</Kbd>
          </span>;
    },
    pressed: c => c.isAgentChatOpen,
    onClick: () => {
      if (!USE_SDK_AGENT_CHAT || !editorStore.isAgentNovaDevEnabled) return;
      editorStore.setAgentChatOpen(!editorStore.isAgentChatOpen);
    }
  }, {
    id: 'build-lightmap',
    label: 'Build Lightmap',
    icon: SunIcon,
    visible: c => c.editorOpened && !c.isPlayMode,
    tooltip: c => {
      if (!sdk?.tools.buildLightmap) {
        return 'Build Lightmap requires the Genesys desktop app';
      }
      if (!project.currentScene) {
        return 'Build Lightmap — open a scene first';
      }
      return 'Build Lightmap';
    },
    disabled: () => !sdk?.tools.buildLightmap || !project.projectFolder || !project.currentScene,
    onClick: () => {
      void state.onBuildLightmapClick();
    }
  }, {
    id: 'scenario',
    label: 'Foundry',
    icon: WandSparklesIcon,
    visible: c => scenarioConfig.enabled && c.editorOpened && !c.isPlayMode,
    tooltip: c => !c.firebaseUser ? 'Foundry - Please sign in to use AI generation' : 'Foundry - Generate with AI',
    disabled: c => !c.firebaseUser,
    pressed: c => c.isSkyboxDialogOpen,
    onClick: () => {
      console.log('SCENARIO button clicked, toggling dialog');
      state.setIsSkyboxDialogOpen(prev => !prev);
    }
  }, {
    id: 'dev-panel',
    label: 'DevPanel',
    icon: FlaskConicalIcon,
    visible: c => c.editorOpened && !c.isPlayMode && c.isDev,
    tooltip: c => c.isDevPanelOpen ? 'Hide DevPanel' : 'Show DevPanel',
    pressed: c => c.isDevPanelOpen,
    onClick: () => editorStore.setDevPanelOpen(!editorStore.isDevPanelOpen)
  }, {
    id: 'upload',
    label: 'Upload Project',
    icon: CloudUploadIcon,
    visible: c => c.projectLoaded && c.editorOpened && !c.isPlayMode,
    tooltip: `Upload Project ${project.projectName}`,
    pressed: c => c.isUploadProjectDialogOpen,
    separatorBefore: true,
    onClick: () => editorActions.uploadProject()
  }];
}

/** Right side controls for the title bar (Play, AI tools, Upload, MCP, Avatar) */
export const TitleBarButtonsRight = observer(() => {
  const pathname = usePathname();
  const {
    firebaseUser
  } = useAuth();
  const [isSkyboxDialogOpen, setIsSkyboxDialogOpen] = useState(false);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const {
    confirmBuildLightmap,
    buildLightmapDialog
  } = useBuildLightmapDialog();
  const onBuildLightmapClick = useCallback(async () => {
    if (!sdk?.tools.buildLightmap) {
      console.warn('[BuildLightmap] SDK not available');
      return;
    }
    if (!project.projectFolder || !project.currentScene) {
      console.warn('[BuildLightmap] No open project or active scene');
      return;
    }
    if (!(await confirmBuildLightmap())) {
      return;
    }
    const result = await sdk.tools.buildLightmap(project.projectFolder, project.currentScene);
    console.log('[BuildLightmap]', result);
    if (!result.success) {
      console.error('[BuildLightmap]', result.message, result.error ?? '');
      return;
    }
    if (sceneStore.world && project.currentScene) {
      await ENGINE.tryApplyLightmapsAfterSceneReady(sceneStore.world, project.currentScene);
    }
  }, [confirmBuildLightmap]);
  const projectOpened = project.isProjectOpened;
  const isPlayMode = editorScene.isPlayMode;
  const isPlayModeStarting = editorScene.isPlayModeStarting;
  const editorOpened = isEditorTitleBarContext(pathname, projectOpened);
  const showPlayControls = shouldShowPlayModeControls(pathname, projectOpened, isPlayMode, isPlayModeStarting);
  const projectLoaded = !!project.project;
  const startPlayMode = (mode?: PlayLaunchMode) => {
    if (mode) {
      store.setPlayLaunchMode(mode);
    }
    void editorActions.setPlayModeActive(true);
  };
  const ctx: TitleBarActionContext = {
    editorOpened,
    isPlayMode,
    projectLoaded,
    firebaseUser: firebaseUser ?? null,
    isDev: IS_DEV,
    isAgentNovaDevEnabled: editorStore.isAgentNovaDevEnabled,
    projectName: project.projectName ?? '',
    isAgentChatOpen: editorStore.isAgentChatOpen,
    isSkyboxDialogOpen,
    isDevPanelOpen: editorStore.isDevPanelOpen,
    isUploadProjectDialogOpen: editorStore.isUploadProjectDialogOpen
  };
  const state: TitleBarRightState = {
    setIsSkyboxDialogOpen,
    onBuildLightmapClick
  };
  const actions = getTitleBarRightActions(ctx, state);
  const visibleActions = actions.filter(a => a.visible(ctx));
  const hasOverflowItems = visibleActions.length > 0;
  const showScenario = scenarioConfig.enabled && ctx.editorOpened && !ctx.isPlayMode;
  const currentLaunchModeMeta = PLAY_LAUNCH_MODE_META[store.playLaunchMode];
  const CurrentLaunchModeIcon = currentLaunchModeMeta.Icon;
  return <>
      {/* Exit Play Mode button - standalone */}
      {showPlayControls && isPlayMode && <Tooltip delayDuration={1000}>
          <TooltipTrigger asChild>
            <TitleBarButton onClick={() => editorActions.setPlayModeActive(false)} disableWithGlobalOverlay={false}>
              <LogOutIcon className="size-4" />
            </TitleBarButton>
          </TooltipTrigger>
          <TooltipContent side="left">
            <span>Exit Play Mode</span>
          </TooltipContent>
        </Tooltip>}

      {/* Start Play Mode button with scene type selector dropdown and multiplayer toggle */}
      {showPlayControls && !isPlayMode && <div className="flex items-center gap-1">
          <div className="flex items-center">
            <Tooltip delayDuration={500}>
              <TooltipTrigger asChild>
                <TitleBarButton onClick={() => startPlayMode()} className="rounded-r-none pr-1">
                  <IconPlay className="size-4" />
                </TitleBarButton>
              </TooltipTrigger>
              <TooltipContent>
                <PlayModeTooltipContent />
              </TooltipContent>
            </Tooltip>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <TitleBarButton className="h-7 gap-0.5 rounded-r-md rounded-l-none border-l border-border/50 px-0" tooltip={'Play mode options'} aria-label={'Play mode options'}>
                  <ChevronDownIcon className="size-3.5" />
                </TitleBarButton>
              </DropdownMenuTrigger>
              <PlayModeLaunchMenu onOpenPreviewSettings={() => setIsPreviewDialogOpen(true)} onStartPlay={startPlayMode} />
            </DropdownMenu>
          </div>
          {/* Multiplayer button — checkbox toggle + client-count slider */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <TitleBarButton pressed={store.isMultiplayerMode}>
                <NetworkIcon className="size-4" />
              </TitleBarButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuCheckboxItem showAsCheckbox checked={store.isMultiplayerMode} onCheckedChange={v => store.setIsMultiplayerMode(!!v)}>
                Multiplayer
              </DropdownMenuCheckboxItem>
              <DropdownMenuSeparator />
              <div className="flex flex-col gap-2 px-2 py-2" onPointerDown={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Clients</span>
                  <span className="text-xs font-semibold tabular-nums">{store.multiplayerClientCount}</span>
                </div>
                <Slider min={1} max={4} step={1} value={[store.multiplayerClientCount]} onValueChange={vals => store.setMultiplayerClientCount(vals[0] ?? 1)} />
                <div className="flex justify-between px-0.5 text-[10px] text-muted-foreground">
                  <span>1</span>
                  <span>2</span>
                  <span>3</span>
                  <span>4</span>
                </div>
              </div>
              <DropdownMenuSeparator />
              <LagSimulationSection title="Server" target="server" enabled={store.multiplayerServerLagEnabled} latencyMs={store.multiplayerServerLagLatencyMs} jitterMs={store.multiplayerServerLagJitterMs} packetLossPercent={store.multiplayerServerLagPacketLossPercent} />
              <DropdownMenuSeparator />
              <LagSimulationSection title="Clients" target="client" enabled={store.multiplayerClientLagEnabled} latencyMs={store.multiplayerClientLagLatencyMs} jitterMs={store.multiplayerClientLagJitterMs} packetLossPercent={store.multiplayerClientLagPacketLossPercent} />
            </DropdownMenuContent>
          </DropdownMenu>
        </div>}

      {showPlayControls && <Separator />}

      {showPlayControls && isPlayMode && sdk?.playMode?.toggleDevTools && <Tooltip delayDuration={1000}>
          <TooltipTrigger asChild>
            <TitleBarButton onClick={() => sdk?.playMode?.toggleDevTools()} disableWithGlobalOverlay={false}>
              <IconDevTools className="size-4" />
            </TitleBarButton>
          </TooltipTrigger>
          <TooltipContent side="left">
            <span>
              Open Play Mode Dev Console <Kbd>F12</Kbd>
            </span>
          </TooltipContent>
        </Tooltip>}

      {/* Default view (xl+ / 1280px+): Show all buttons inline from config */}
      <div className="hidden xl:flex items-center gap-2">
        {visibleActions.map(action => <Fragment key={action.id}>
            {action.separatorBefore && <Separator />}
            {action.customRender ? action.customRender() : (() => {
          const Icon = action.icon;
          return <TitleBarButton onClick={action.onClick} disabled={action.disabled?.(ctx)} buttonText={COMPACT_VIEW ? undefined : action.label} tooltip={typeof action.tooltip === 'function' ? action.tooltip(ctx) : action.tooltip} pressed={action.pressed?.(ctx)}>
                    <Icon className="size-3.5" />
                  </TitleBarButton>;
        })()}
          </Fragment>)}
      </div>

      {/* Collapsed view (<xl / <1280px): Overflow menu from config */}
      {hasOverflowItems && <div className="flex xl:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <TitleBarButton tooltip="More actions">
                <MoreHorizontalIcon className="size-4" />
              </TitleBarButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {visibleActions.filter(a => !a.customRender).map(action => {
            const Icon = action.icon;
            return <DropdownMenuItem key={action.id} onClick={action.onClick} disabled={action.disabled?.(ctx)} className="flex items-center gap-2 cursor-pointer">
                    <Icon className="size-3.5" />
                    <span>{action.label}</span>
                  </DropdownMenuItem>;
          })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>}

      {showScenario && <ScenarioGenerationDialog isOpen={isSkyboxDialogOpen} onOpenChange={setIsSkyboxDialogOpen} />}
      <PlayPreviewSettingsDialog isOpen={isPreviewDialogOpen} onOpenChange={setIsPreviewDialogOpen} onStartPlay={mode => {
      setIsPreviewDialogOpen(false);
      startPlayMode(mode);
    }} />
      {buildLightmapDialog}

      {showPlayControls && <Separator />}

      {/* User avatar - always visible */}
      <UserAvatar />
    </>;
});
TitleBarButtonsRight.displayName = 'TitleBarButtonsRight';
function _temp(e) {
  return e.stopPropagation();
}
function _temp2(e_0) {
  return e_0.stopPropagation();
}